import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { docsEqual, type AdvisorDoc } from '@agio/core/domain';
import { advisorExtensions } from './advisorExtensions.js';
import { EditorToolbar } from './EditorToolbar.js';

const EMPTY: AdvisorDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

/**
 * The rich-text editor for the advisor summary (RTL, Hebrew toolbar).
 *
 * `value` is what to show when it CHANGES from outside (a version copied in,
 * a draft reset) — typing does not round-trip through it, so the cursor never
 * jumps. `onChange` reports every edit as the editor's JSON document.
 */
export function AdvisorEditor({
  value,
  onChange,
  label,
}: {
  value: AdvisorDoc | null;
  onChange: (doc: AdvisorDoc) => void;
  /** Accessible name of the writing area. */
  label: string;
}) {
  const editor = useEditor({
    extensions: advisorExtensions({ editable: true }),
    content: value ?? EMPTY,
    editorProps: {
      attributes: {
        dir: 'rtl',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
        class: 'min-h-48 px-4 py-3 outline-none',
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.getJSON() as AdvisorDoc),
  });

  // Replace the content only when the outside value is genuinely different.
  const incoming = JSON.stringify(value ?? EMPTY);
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const next = JSON.parse(incoming) as AdvisorDoc;
    // Compared by structure, not text: a body read back from the database may list its keys in another order.
    if (!docsEqual(editor.getJSON(), next)) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, incoming]);

  return (
    <div className="overflow-hidden rounded-lg border border-input bg-card focus-within:ring-2 focus-within:ring-ring">
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} dir="rtl" className="advisor-prose" />
    </div>
  );
}
